import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import BlockIcon from "@mui/icons-material/Block";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useState } from "react";
import { QueryClient, useMutation } from "react-query";

import Search from "components/Search/Search";
import { handleError } from "helpers/utils";

import { IDepartments, IDepartmentsInput } from "models/departments";
import {
  DepartmentsCells,
  addDepartment,
  deleteDepartment,
  editDepartment,
  getDepartments,
} from "framework/departments";
import AddDepartment from "./partials/AddDepartment/AddDepartment";
import EditDepartment from "./partials/EditDepartment/EditDepartment";
import DeleteDepartment from "./partials/DeleteDepartment/DeleteDepartment";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import useFetchSection from "hooks/useFetchSection";

interface Props {
  feature: Feature;
}

const Departments: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const user = getUserData();
  const { colors } = user;
  const [FilterDepartments, setFilterDepartments] = useState<IDepartments[]>(
    [] as IDepartments[]
  );
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedDepartment, setSelectedDepartment] = useState<IDepartments>(
    {} as IDepartments
  );
  const [inputObj, setInputObj] = useState<IDepartmentsInput>(
    {} as IDepartmentsInput
  );
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addDepartmentsMutation = useMutation({
    mutationFn: (createInput: IDepartmentsInput) => {
      return addDepartment(createInput);
    },
  });

  const editDepartmentsMutation = useMutation({
    mutationFn: (data: { createInput: IDepartmentsInput; id: string }) => {
      return editDepartment(data.createInput, data.id);
    },
  });

  const deleteDepartmentsMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteDepartment(id);
    },
  });

  const {
    loading,
    data: Departments,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getDepartments(),
    fetchPermission: () => getPermission(12),
    section: "Departments",
    onSuccess: (data) => setFilterDepartments(data),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Country: IDepartments) => {
    setSelectedDepartment(Country);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedDepartment({} as IDepartments);
  };

  const handleOpenDelete = (Country: IDepartments) => {
    setSelectedDepartment(Country);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedDepartment({} as IDepartments);
  };

  const getBodyCells = (row: IDepartments) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.name || !inputObj.nameAr)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addDepartmentsMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Departments added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as IDepartmentsInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: IDepartmentsInput = {
        name: inputObj.name || selectedDepartment.name,
        nameAr: inputObj.nameAr || selectedDepartment.nameAr,
      };
      await editDepartmentsMutation.mutateAsync({
        createInput,
        id: selectedDepartment.id,
      });
      setAlert({
        open: true,
        message: "Departments updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as IDepartmentsInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteDepartmentsMutation.mutateAsync(selectedDepartment.id);
      setAlert({
        open: true,
        message: "Departments deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterDepartments(Departments!);
    let filtered = Departments!.filter((department) =>
      department.name.toLowerCase().includes(value.toLowerCase())
      || department.nameAr?.toLowerCase()?.includes(value.toLowerCase())
    );
    setFilterDepartments(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />

            {permission?.hasModifyPermission && (
              <Tooltip title="Add Department">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterDepartments.length > 0 && (
          <List
            headCells={DepartmentsCells}
            data={FilterDepartments}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterDepartments || FilterDepartments.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_department_header")}
              message={t("T_no_department_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddDepartment
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditDepartment
          open={openEdit}
          loading={loading2}
          department={selectedDepartment}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteDepartment
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Departments;
